/**
 * The page as media: zoom, rotate, pan, fit, fullscreen.
 *
 * The overlay has to move with the image or it stops meaning anything, so the transform
 * is applied to one stage that holds both the page and its region boxes. Boxes are
 * positioned as percentages of that stage, so they scale and rotate for free and stay
 * exactly on the ink.
 *
 * Fit is computed rather than left to CSS because rotation changes which dimension
 * binds: a portrait page turned 90° is limited by the pane's height, not its width.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 12;
const STEP = 1.25;

export interface ViewerState {
  /** Absolute scale: 1 means one page pixel per screen pixel. */
  scale: number;
  rotation: number;
  panning: boolean;
}

function clampZoom(scale: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, scale));
}

export function MediaViewer({
  width,
  height,
  children,
  onStateChange,
}: {
  /** Natural size of the page in its own pixels — the frame the overlay is measured in. */
  width: number;
  height: number;
  children: ReactNode;
  onStateChange?: (state: ViewerState) => void;
}) {
  const frame = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const [rotation, setRotation] = useState(0);
  /** null means "follow the fit"; a number pins an absolute scale. */
  const [pinned, setPinned] = useState<number | null>(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);

  // Fit has to be measured: the pane's size is decided by the layout, not by us.
  useLayoutEffect(() => {
    const node = frame.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setBox({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const quarterTurned = rotation % 180 !== 0;
  const shownWidth = quarterTurned ? height : width;
  const shownHeight = quarterTurned ? width : height;
  const fitScale =
    box.width && box.height
      ? Math.min(box.width / Math.max(shownWidth, 1), box.height / Math.max(shownHeight, 1))
      : 0;
  const scale = pinned ?? fitScale;
  const isFit = pinned === null;

  useEffect(() => {
    onStateChange?.({ scale, rotation, panning: pinned !== null });
  }, [scale, rotation, pinned, onStateChange]);

  const zoomBy = useCallback(
    (factor: number) => {
      setPinned((current) => clampZoom((current ?? fitScale) * factor));
    },
    [fitScale],
  );

  const reset = useCallback(() => {
    setPinned(null);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  }, []);

  // Keyboard, once the viewer has focus: the shortcuts every image viewer has.
  function onKeyDown(event: React.KeyboardEvent) {
    const handlers: Record<string, () => void> = {
      "+": () => zoomBy(STEP),
      "=": () => zoomBy(STEP),
      "-": () => zoomBy(1 / STEP),
      "0": reset,
      r: () => setRotation((r) => (r + 90) % 360),
      R: () => setRotation((r) => (r + 270) % 360),
      f: () => setPinned(null),
    };
    const handler = handlers[event.key];
    if (!handler) return;
    event.preventDefault();
    handler();
  }

  function onWheel(event: React.WheelEvent) {
    // Trackpad pinch arrives as ctrl+wheel; plain wheel is left alone so a nested
    // scroll region still behaves.
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    zoomBy(event.deltaY < 0 ? STEP : 1 / STEP);
  }

  function onPointerDown(event: React.PointerEvent) {
    if (isFit) return; // nothing to pan to while the whole page is visible
    (event.target as Element).setPointerCapture?.(event.pointerId);
    drag.current = { x: event.clientX, y: event.clientY, panX: pan.x, panY: pan.y };
  }
  function onPointerMove(event: React.PointerEvent) {
    const start = drag.current;
    if (!start) return;
    setPan({
      x: start.panX + (event.clientX - start.x),
      y: start.panY + (event.clientY - start.y),
    });
  }
  function onPointerUp() {
    drag.current = null;
  }

  async function toggleFullscreen() {
    const node = frame.current?.parentElement;
    if (!node) return;
    if (document.fullscreenElement) await document.exitFullscreen();
    else await node.requestFullscreen().catch(() => undefined);
  }

  return (
    <>
      <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-hair bg-hair-soft px-3 py-1.5">
        <Control onClick={() => zoomBy(1 / STEP)} label="Zoom out" disabled={scale <= MIN_ZOOM}>
          <svg viewBox="0 0 24 24" {...ICON}>
            <circle cx="11" cy="11" r="6.5" />
            <path d="M8 11h6M16 16l4.5 4.5" />
          </svg>
        </Control>
        <span className="tnum w-14 text-center font-mono text-[10.5px] text-dim">
          {scale ? `${Math.round(scale * 100)}%` : "—"}
        </span>
        <Control onClick={() => zoomBy(STEP)} label="Zoom in" disabled={scale >= MAX_ZOOM}>
          <svg viewBox="0 0 24 24" {...ICON}>
            <circle cx="11" cy="11" r="6.5" />
            <path d="M8 11h6M11 8v6M16 16l4.5 4.5" />
          </svg>
        </Control>

        <span className="mx-1 h-4 w-px bg-hair" />

        <Toggle onClick={() => setPinned(null)} label="Fit the page in view" active={isFit}>
          fit
        </Toggle>
        <Toggle onClick={() => setPinned(1)} label="Actual size" active={pinned === 1}>
          1:1
        </Toggle>

        <span className="mx-1 h-4 w-px bg-hair" />

        <Control onClick={() => setRotation((r) => (r + 270) % 360)} label="Rotate left">
          <svg viewBox="0 0 24 24" {...ICON}>
            <path d="M4 12a8 8 0 1 0 2.3-5.6" />
            <path d="M4 4v5h5" />
          </svg>
        </Control>
        <Control onClick={() => setRotation((r) => (r + 90) % 360)} label="Rotate right">
          <svg viewBox="0 0 24 24" {...ICON}>
            <path d="M20 12a8 8 0 1 1-2.3-5.6" />
            <path d="M20 4v5h-5" />
          </svg>
        </Control>
        <Control onClick={reset} label="Reset view">
          <svg viewBox="0 0 24 24" {...ICON}>
            <path d="M12 5v14M5 12h14" transform="rotate(45 12 12)" />
          </svg>
        </Control>

        <span className="ms-auto flex items-center gap-1.5">
          <span className="hidden font-mono text-[9.5px] tracking-[0.12em] text-faint uppercase sm:inline">
            {rotation ? `${rotation}° · ` : ""}
            {isFit ? "fit" : "drag to pan"}
          </span>
          <Control onClick={() => void toggleFullscreen()} label="Fullscreen">
            <svg viewBox="0 0 24 24" {...ICON}>
              <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
            </svg>
          </Control>
        </span>
      </div>

      <div
        ref={frame}
        tabIndex={0}
        role="group"
        aria-label="Page viewer — arrow-free zoom with plus and minus, r to rotate, 0 to reset"
        onKeyDown={onKeyDown}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className={`relative min-h-0 flex-1 overflow-hidden bg-ground-soft outline-none ${
          isFit ? "" : drag.current ? "cursor-grabbing" : "cursor-grab"
        }`}
      >
        <div
          className="absolute start-1/2 top-1/2 border border-hair-strong bg-page-bg"
          style={{
            width: `${width}px`,
            height: `${height}px`,
            transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) rotate(${rotation}deg) scale(${scale || 0.001})`,
            transformOrigin: "center",
            transition: drag.current ? "none" : "transform 180ms var(--ease)",
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
}

const ICON = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "square" as const,
  className: "size-full",
};

function Control({
  onClick,
  label,
  disabled = false,
  children,
}: {
  onClick: () => void;
  label: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="grid size-6 place-items-center border border-transparent text-dim transition-colors duration-150 hover:border-hair hover:text-gold disabled:opacity-30 disabled:hover:border-transparent disabled:hover:text-dim"
    >
      <span className="size-3.5">{children}</span>
    </button>
  );
}

function Toggle({
  onClick,
  label,
  active,
  children,
}: {
  onClick: () => void;
  label: string;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-pressed={active}
      className={`border px-2 py-0.5 font-mono text-[10px] tracking-[0.12em] uppercase transition-colors duration-150 ${
        active
          ? "border-gold-surface bg-gold-surface font-bold text-on-gold"
          : "border-hair text-dim hover:text-gold"
      }`}
    >
      {children}
    </button>
  );
}
